/**
 * 把一个浮层做成可拖动，并把落点记在本地。
 *
 * 用途是白板上的本地摄像头小窗：它默认停在右下角，
 * 而右下角恰恰是板书写到最后会用到的地方 —— 讲课的人没法让画面躲开，
 * 只能绕着它写。所有会议软件的自画面都能拖，原因就在这里。
 *
 * 位置存的是**距离容器左上角的比例**，不是像素：
 * 换一块屏幕、拉一下窗口、收起侧栏，容器宽高都会变，
 * 存像素的话小窗会跑到容器外面去，而且再也拖不回来。
 */

const KEY = 'whatylive.localPreview.pos'

export type Pos = { xr: number; yr: number }

/** 读上次的落点；没存过或存坏了都回落到右下角。 */
export function loadPos(): Pos {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return DEFAULT_POS
    const p = JSON.parse(raw) as Pos
    if (typeof p?.xr !== 'number' || typeof p?.yr !== 'number') return DEFAULT_POS
    if (!isFinite(p.xr) || !isFinite(p.yr)) return DEFAULT_POS
    return { xr: clamp01(p.xr), yr: clamp01(p.yr) }
  } catch {
    // 隐私模式下 localStorage 读写会直接抛，不能让它把整个预览拖垮。
    return DEFAULT_POS
  }
}

/** 记下落点。存不进去就算了，位置丢了不影响这次上课。 */
export function savePos(p: Pos): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p))
  } catch {
    /* 忽略 */
  }
}

/** 默认位置：右下角。0.5 是「一半」，这里取右/下各留一点边距后的比例。 */
export const DEFAULT_POS: Pos = { xr: 1, yr: 1 }

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/**
 * 把「比例位置」换算成实际的 left/top 像素。
 *
 * 换算时要把浮层自身的尺寸算进去：比例 1 表示贴着右/下边缘，
 * 直接拿 容器宽 × 1 当 left 的话，浮层会整个跑到容器外面。
 */
export function toPx(p: Pos, box: { w: number; h: number }, el: { w: number; h: number },
                     margin = 16): { left: number; top: number } {
  const maxL = Math.max(margin, box.w - el.w - margin)
  const maxT = Math.max(margin, box.h - el.h - margin)
  return {
    left: margin + (maxL - margin) * p.xr,
    top: margin + (maxT - margin) * p.yr,
  }
}

/** 实际像素反推成比例，供保存用。 */
export function toRatio(left: number, top: number, box: { w: number; h: number },
                        el: { w: number; h: number }, margin = 16): Pos {
  const maxL = Math.max(margin, box.w - el.w - margin)
  const maxT = Math.max(margin, box.h - el.h - margin)
  const spanX = maxL - margin
  const spanY = maxT - margin
  return {
    xr: spanX > 0 ? clamp01((left - margin) / spanX) : 1,
    yr: spanY > 0 ? clamp01((top - margin) / spanY) : 1,
  }
}
